<template lang="pug">
.screenshot-activity-overlay
  .activity-actions-wrapper
    .select-action(v-if="canSelectActivities")
      .rounded-checkbox-wrapper
        //- @click.stop here and elsewhere is to prevent clicks from leaking up to the gallery click handler
        input.rounded-checkbox.js-select-activity.no-action(
          v-model="selected"
          type="checkbox"
          @click.stop
        )
        label
    .activity-actions
      template(v-if="hasScreens && !screensDeleted && canDeleteScreens")
        a.text-superlight(
          v-if="canDeleteScreens"
          v-tooltip
          :title="deleteScreensTooltip"
          :disabled="!canDeleteScreensWithOverride"
          data-testid="delete-screens-button"
          @click.stop="onDeleteScreens"
        )
          i.hi.hi-remove-screenshot.hi-16.no-action.text-white
        span(
          v-else
          v-tooltip
          title="You do not have permission to delete these screenshots"
        )
          i.hi.hi-remove-screenshot.hi-16.no-action.is-muted.text-white
      a.move-activity(
        v-if="canChangeActivityModify"
        v-tooltip
        title="Move all activity in this time span"
        data-testid="move-activity-button"
        @click.stop="showMoveActivityDialog"
      )
        i.hi.hi-move-xs.hi-12.no-action.text-white
      a.delete-activity(
        v-if="userTrackingEnabled"
        v-tooltip
        :title="deleteActivityHint"
        :disabled="!canDeleteActivityWithOverride ? 'disabled' : null"
        data-testid="delete-activity-button"
        @click.stop="onDeleteActivity"
      )
        i.hi.hi-bin.hi-12.no-action.text-white
      span(
        v-else
        v-tooltip
        title="Time tracking disabled"
      )
        i.hi.hi-bin.hi12.no-action.is-muted.text-white
      template(v-if="lockedActivity && !canOverrideLocked")
        ActivitiesLockedPopover(:associated-records="activity.associated_activities")
  slot
  TimeEntriesOverrideDialog(
    v-if="showOverrideDialog"
    v-model:show="showOverrideDialog"
    type="delete"
    :affected-records="affectedRecords"
    @submit="confirmOverride"
    @close="handleOverrideCancel"
  )
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ActivitiesLockedPopover from './ActivitiesLockedPopover.vue';
import TimeEntriesOverrideDialog from '~/components/time_entries/TimeEntriesOverrideDialog.vue';
import { TimeEntryOverrideApprovedTimesheet } from '~/mixins';

export default {
  name: 'ActivitiesBlockActions',
  components: {
    ActivitiesLockedPopover,
    TimeEntriesOverrideDialog,
  },
  mixins: [TimeEntryOverrideApprovedTimesheet],
  inject: {
    timesheetScreensOverrideHandler: { default: null },
  },
  props: {
    allProjectNames: {
      type: String,
      required: true,
    },
    activity: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    showOverrideDialog: false,
    affectedRecords: {},
    validationToken: null,
    previousValidationToken: null,
    timesheetRedirectUrl: null,
    overrideAction: null,
  }),
  computed: {
    ...mapGetters(['organizationId', 'queryUserId']),
    ...mapState({
      selectedTimeSlots: (state) => state.selected_time_slots.map((ts) => ts.timeSlot),
    }),
    deleteScreensTooltip() {
      if (!this.canDeleteScreensWithOverride) {
        return 'You do not have permission to delete time';
      }

      return this.hasScreens && this.screenCount === 1
        ? 'Delete selected screenshot from this time span'
        : `Delete all screenshots (${this.screenCount}) from this time span for ${this.allProjectNames}`;
    },
    screenCount() {
      return this.activity?.screens?.length;
    },
    hasScreens() {
      const screenCount = typeof this.screenCount === 'number' ? this.screenCount > 0 : false;

      return !this.activity.idle_time && screenCount;
    },
    screensDeleted() {
      return (this.activity?.screens || []).filter((screen) => !screen.deleted).length === 0;
    },
    permissions() {
      return this.activity?.permissions;
    },
    canEditTimesheet() {
      return this.permissions?.can_edit_timesheet;
    },
    canDeleteScreens() {
      return this.permissions?.can_delete_screens;
    },
    canDeleteActivity() {
      return this.permissions?.can_delete_activity;
    },
    canModifyActivity() {
      return this.permissions?.can_modify_activity;
    },
    canChangeActivityModify() {
      return this.permissions?.can_change_activity_modify;
    },
    canChangeActivityDelete() {
      return this.permissions?.can_change_activity_delete;
    },
    lockedActivity() {
      return Boolean(this.activity?.locked);
    },
    canOverrideLocked() {
      return Boolean(this.activity?.overrideable);
    },
    canSelectActivities() {
      return (
        (this.canChangeActivityDelete ||
          this.canChangeActivityModify ||
          (this.lockedActivity && this.canOverrideLocked)) &&
        (!this.lockedActivity || this.canOverrideLocked)
      );
    },
    canDeleteActivityWithOverride() {
      return this.canChangeActivityDelete || (this.lockedActivity && this.canOverrideLocked);
    },
    canDeleteScreensWithOverride() {
      return this.canChangeActivityDelete || (this.lockedActivity && this.canOverrideLocked);
    },
    deleteActivityHint() {
      if (this.lockedActivity && !this.canOverrideLocked) {
        return 'This time is locked and cannot be deleted';
      }

      return this.canDeleteActivityWithOverride
        ? 'Delete all activity in this time span'
        : 'You do not have permission to delete time';
    },
    userTrackingEnabled() {
      return this.activity?.user_tracking_enabled;
    },
    associatedRecords() {
      return this.activity?.associated_records;
    },
    timeSlot() {
      return this.activity?.time_slot;
    },
    isSelected() {
      return this.selectedTimeSlots.includes(this.timeSlot);
    },
    selected: {
      get() {
        return this.isSelected;
      },
      set() {
        if (this.isSelected) {
          this.deselectTimeSlots([this.timeSlot]);
        } else {
          this.selectTimeSlots([
            {
              timeSlot: this.timeSlot,
              screens: this.activity.screens,
              tracked: this.activity.tracked,
            },
          ]);
        }
      },
    },
  },
  methods: {
    ...mapActions(['deleteScreens', 'deleteActivity', 'selectTimeSlots', 'deselectTimeSlots']),
    error() {
      this.$appMessage('Something went wrong. Please try again.');
    },
    success(response) {
      if (response?.action === 'confirm-override') {
        if (this.timesheetScreensOverrideHandler) {
          this.timesheetScreensOverrideHandler(response, (token, timesheetRedirectUrl) =>
            this.confirmOverrideWithToken(token, timesheetRedirectUrl)
          );

          return;
        }
        this.affectedRecords = response.associated_records || {};
        this.previousValidationToken = this.validationToken;
        this.validationToken = response.validation_token;
        this.timesheetRedirectUrl = response.timesheet_redirect_url;
        this.showOverrideDialog = true;

        return;
      }

      const message = response?.message ?? 'Activity deleted successfully.';

      this.$appMessage(message);
      if (response?.success && (this.timesheetRedirectUrl || response?.timesheet_redirect_url)) {
        if (response?.timesheet_redirect_url) {
          this.timesheetRedirectUrl = response.timesheet_redirect_url;
        }
        this.redirectToTimesheet();
      }
    },
    onDeleteScreens() {
      if (!this.canDeleteScreensWithOverride) {
        return false;
      }

      let confirmation;

      if (this.screenCount === 1) {
        confirmation = 'Are you sure you want to delete selected screenshot?';
      } else {
        confirmation =
          `Are you sure you want to delete all screenshots ` +
          `(${this.screenCount}) from this time span ` +
          `(${this.activity.slot_span}) for ${this.allProjectNames}?`;
      }

      if (confirm(confirmation)) {
        this.overrideAction = 'deleteScreens';
        this.deleteScreens({ timeSlots: [this.activity.time_slot] }).then(this.success, this.error);
      }
    },
    showMoveActivityDialog() {
      const urlParams =
        `?organization_id=${this.organizationId}` +
        `&time_slots=[${this.activity.time_slot}]` +
        `&user_id=${this.queryUserId}`;

      AjaxDialog.run(`/activities/move_activity_dialog.dialog${urlParams}`);
    },
    onDeleteActivity() {
      if (!this.canDeleteActivityWithOverride) {
        return false;
      }

      const confirmation =
        `Are you sure you want to delete all activity ` +
        `in this time span (${this.activity.slot_span}) ` +
        `for ${this.allProjectNames}?`;

      if (confirm(confirmation)) {
        this.overrideAction = 'deleteActivity';
        this.deleteActivity({ timeSlots: [this.activity.time_slot] }).then(this.success, this.error);
      }
    },
    confirmOverride() {
      this.showOverrideDialog = false;
      const payload = {
        timeSlots: [this.activity.time_slot],
        confirmed: true,
        validationToken: this.validationToken,
      };

      if (this.overrideAction === 'deleteScreens') {
        this.deleteScreens(payload).then(this.success, this.error);
      } else {
        this.deleteActivity(payload).then(this.success, this.error);
      }
    },
    /** Used when parent (e.g. timesheet dialog) handles the override UI: set token/url then use same confirm flow as local dialog for action-tracking. */
    confirmOverrideWithToken(token, timesheetRedirectUrl) {
      if (timesheetRedirectUrl) {
        this.timesheetRedirectUrl = timesheetRedirectUrl;
      }
      this.validationToken = token;
      this.confirmOverride();
    },
    performOverrideConfirm(token, timesheetRedirectUrl) {
      if (timesheetRedirectUrl) {
        this.timesheetRedirectUrl = timesheetRedirectUrl;
      }
      this.showOverrideDialog = false;
      const payload = {
        timeSlots: [this.activity.time_slot],
        confirmed: true,
        validationToken: token,
      };

      if (this.overrideAction === 'deleteScreens') {
        this.deleteScreens(payload).then(this.success, this.error);
      } else {
        this.deleteActivity(payload).then(this.success, this.error);
      }
    },
    handleOverrideCancel() {
      this.showOverrideDialog = false;
      this.affectedRecords = {};
      this.validationToken = null;
      this.timesheetRedirectUrl = null;
      this.overrideAction = null;
    },
  },
};
</script>
